<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useAirjumpStore } from '../../store/useAirjumpStore.js';
import { rpc } from '../../composables/useRpc.js';
import QuickQuotationModal from './modals/QuickQuotationModal.vue';
import SendQuotationModal from './modals/SendQuotationModal.vue';
import PriceListModal from './modals/PriceListModal.vue';
import ClientLinkModal from './modals/ClientLinkModal.vue';
import BurbujaCalculator from './modals/BurbujaCalculator.vue';
import PortfolioModal from './modals/PortfolioModal.vue';
import DeleteConfirmModal from './modals/DeleteConfirmModal.vue';

const props = defineProps({
  chat: { type: Object, default: () => ({}) },
});

const store = useAirjumpStore();
const summaryExpanded = ref(false);
const addingToCrm = ref(false);

async function addToCrm() {
  if (!phone.value || addingToCrm.value) return;
  addingToCrm.value = true;
  try {
    await rpc('create_and_link_client', {
      body: { phone: phone.value, conversation_id: store.convId },
    });
    await store.loadConversation(phone.value);
  } catch (err) {
    console.error('Error adding to CRM:', err);
  }
  addingToCrm.value = false;
}

// ── Phone from conversation sender ──
const phone = computed(() => {
  const sender = props.chat?.meta?.sender;
  if (!sender) return '';
  const raw = sender.phone_number || sender.identifier || '';
  return raw.replace(/^\+/, '').replace(/\D/g, '');
});

// ── Watch conversation changes ──
watch(
  () => props.chat?.id,
  () => {
    if (phone.value) {
      store.loadConversation(phone.value);
      store.loadCompanyData(phone.value);
    }
    summaryExpanded.value = false;
  }
);

onMounted(() => {
  store.loadGlobals();
  if (phone.value) {
    store.loadConversation(phone.value);
    store.loadCompanyData(phone.value);
  }
});

// ── Docs ──
function openQuote(id) {
  window.open(`https://crm.desde1980.com/quote-builder/${id}`, '_blank');
}

// ── Tab 3: Acciones ──
const actions = [
  { key: 'cotizar', label: 'Cotizar', icon: 'i-lucide-zap' },
  { key: 'lista', label: 'Lista', icon: 'i-lucide-list' },
  { key: 'portfolio', label: 'Portfolio', icon: 'i-lucide-image' },
  { key: 'vincular', label: 'Vincular', icon: 'i-lucide-link' },
];

function openAction(key) {
  switch (key) {
    case 'cotizar': store.showQuotation = true; break;
    case 'lista': store.showPriceList = true; break;
    case 'portfolio': store.showPortfolio = true; break;
    case 'vincular': store.showClientLink = true; break;
  }
}

async function handleDelete() {
  if (store.deleteTarget?.type === 'cotización') {
    await store.deleteQuotation(store.deleteTarget.id);
  } else {
    await store.deletePriceList(store.deleteTarget.id);
  }
  store.showDelete = false;
  store.deleteTarget = null;
}
</script>

<template>
  <div class="aj-crm-panel">
    <!-- Loading state -->
    <div v-if="store.convLoading" class="aj-loading">
      <span class="aj-spinner" />
    </div>

    <template v-else>
      <!-- AI Summary (collapsible) -->
      <div v-if="store.aiSummary" class="aj-summary" @click="summaryExpanded = !summaryExpanded">
        <p class="aj-summary-text" :class="{ 'aj-summary--expanded': summaryExpanded }">
          {{ store.aiSummary }}
        </p>
      </div>

      <!-- Add to CRM button (when not linked) -->
      <div v-if="!store.clientId && phone" class="aj-add-crm">
        <button class="aj-add-crm-btn" :disabled="addingToCrm" @click="addToCrm">
          <span class="i-lucide-user-plus" />
          {{ addingToCrm ? 'Agregando...' : 'Agregar al CRM' }}
        </button>
      </div>

      <!-- Burbuja Calculator -->
      <BurbujaCalculator
        v-if="store.productFamily === 'arq_textil' && (store.productSubtag === 'burbuja' || store.productSubtag === 'natatorio')"
        :phone="phone"
        :conversation-id="store.convId"
      />

      <!-- Documents -->
      <div class="aj-docs">
        <div class="aj-section">
          <div class="aj-section-header">
            <div class="aj-section-title">Documentos</div>
            <div class="aj-section-actions">
              <button v-for="a in actions" :key="a.key" class="aj-native-btn" :title="a.label" @click="openAction(a.key)"><span :class="a.icon" /></button>
            </div>
          </div>
          <div v-for="q in store.quotations" :key="q.id" class="aj-doc-item">
            <span class="aj-doc-label">{{ q.title || 'Sin titulo' }}</span>
            <span class="aj-doc-actions">
              <button class="aj-native-btn" title="Enviar" @click="store.sendQuotationPreselect = q.id; store.showSendQuotation = true"><span class="i-lucide-send" /></button>
              <button class="aj-native-btn" title="Editar" @click="openQuote(q.id)"><span class="i-lucide-pencil" /></button>
              <button class="aj-native-btn aj-native-btn--danger" title="Eliminar" @click="store.deleteTarget = { type: 'cotización', id: q.id, title: q.title }; store.showDelete = true"><span class="i-lucide-trash-2" /></button>
            </span>
          </div>
        </div>

        <div v-if="store.priceLists.length" class="aj-section">
          <div class="aj-section-title">Listas de precios</div>
          <div v-for="pl in store.priceLists" :key="pl.id" class="aj-doc-item">
            <span class="aj-doc-label">{{ pl.name || 'Lista de precios' }}</span>
            <span class="aj-doc-actions">
              <button class="aj-native-btn" title="Enviar" @click="store.sendPriceList(pl.id)"><span class="i-lucide-send" /></button>
              <button class="aj-native-btn aj-native-btn--danger" title="Eliminar" @click="store.deleteTarget = { type: 'lista', id: pl.id, title: pl.name || 'Lista' }; store.showDelete = true"><span class="i-lucide-trash-2" /></button>
            </span>
          </div>
        </div>

        <div v-if="!store.quotations.length && !store.priceLists.length" class="aj-empty">Sin documentos</div>
      </div>
    </template>

    <!-- Modals -->
    <QuickQuotationModal :show="store.showQuotation" :phone="phone" :conversation-id="store.convId" :client-id="store.clientId" @close="store.showQuotation = false" @created="store.loadConversation(phone); store.showQuotation = false" />
    <SendQuotationModal :show="store.showSendQuotation" :conversation-id="store.convId" :quotations="store.quotations" :preselected-id="store.sendQuotationPreselect" @close="store.showSendQuotation = false" @sent="store.showSendQuotation = false" />
    <PriceListModal :show="store.showPriceList" :phone="phone" :conversation-id="store.convId" @close="store.showPriceList = false" @created="store.refreshDocuments(); store.showPriceList = false" />
    <ClientLinkModal :show="store.showClientLink" :phone="phone" :conversation-id="store.convId" @close="store.showClientLink = false" @linked="store.loadConversation(phone); store.showClientLink = false" />
    <PortfolioModal :show="store.showPortfolio" :phone="phone" :conversation-id="store.convId" @close="store.showPortfolio = false" />
    <DeleteConfirmModal :show="store.showDelete" :title="'Eliminar ' + (store.deleteTarget?.type || '')" :message="'¿Eliminar ' + (store.deleteTarget?.title || '') + '?'" @close="store.showDelete = false; store.deleteTarget = null" @confirm="handleDelete" />
  </div>
</template>

<style scoped>
.aj-crm-panel {
  padding: 8px 0;
  font-size: 13px;
  color: rgb(var(--slate-12));
}

.aj-add-crm {
  padding: 4px 12px 8px;
}

.aj-add-crm-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px dashed rgb(var(--slate-5, 220 220 224));
  border-radius: 8px;
  background: transparent;
  color: rgb(var(--slate-11, 96 100 108));
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}

.aj-add-crm-btn:hover {
  border-color: rgb(var(--blue-8, 62 99 221));
  color: rgb(var(--blue-9, 62 99 221));
  background: rgb(var(--blue-2, 237 244 254));
}

.aj-add-crm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.aj-loading {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.aj-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgb(var(--slate-4));
  border-top-color: rgb(var(--blue-9));
  border-radius: 50%;
  animation: aj-spin 0.6s linear infinite;
}

@keyframes aj-spin {
  to { transform: rotate(360deg); }
}

/* Content */
.aj-docs, .aj-actions-row {
  padding: 0 12px;
}

/* No tags CSS needed — moved to header TagsPill */

/* Autopilot is in header pill, not here */

/* AI Summary */
.aj-summary {
  padding: 6px 12px;
  cursor: pointer;
}

.aj-summary-text {
  font-size: 12px;
  color: rgb(var(--slate-11));
  line-height: 1.4;
  cursor: pointer;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
}

.aj-summary--expanded {
  -webkit-line-clamp: unset;
  overflow: visible;
}

/* Docs tab */
.aj-section {
  margin-bottom: 12px;
}

.aj-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.aj-section-actions {
  display: flex;
  gap: 2px;
}

.aj-section-title {
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--slate-9));
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.aj-doc-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid rgb(var(--slate-3));
}

.aj-doc-label {
  font-size: 12px;
  color: rgb(var(--slate-12));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.aj-doc-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

.aj-native-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: rgb(var(--slate-9, 142 142 147) / 0.1);
  color: rgb(var(--slate-12));
  cursor: pointer;
  transition: all 0.1s;
  font-size: 14px;
}

.aj-native-btn:hover {
  background: rgb(var(--slate-9, 142 142 147) / 0.2);
}

.aj-native-btn--danger {
  color: rgb(var(--ruby-11, 190 46 51));
  background: rgb(var(--ruby-9, 229 72 77) / 0.1);
}

.aj-native-btn--danger:hover {
  background: rgb(var(--ruby-9, 229 72 77) / 0.2);
}

.aj-empty {
  font-size: 12px;
  color: rgb(var(--slate-9));
  padding: 8px 0;
  font-style: italic;
}

</style>
